import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import swal from 'sweetalert';
import 'react-quill/dist/quill.snow.css';
import "../includes/react-quill-custom-stylesheet.css"
import { Redirect } from 'react-router-dom';
import Select from 'react-select';
import { Modal } from 'react-bootstrap';
import PageError from '../common_component/PageError.js';
import { getCookie } from '../common_component/cookie.js';
import { useTheme } from "./theme-provider.js";
import { useAuthData } from "./auth-provider.js";

function SetUpEcommercePaymentInfo() {
	const [state, setstate] = useState(2);
	const { theme, dropdownStyleDark } = useTheme();
	const [navtype, setnavtype] = useState("Payment configuration");

	const [platform, setPlatform] = useState("");
	const [currentPlatformData, setCurrentPlatformData] = useState(null);
	const [platformFields, setPlatformFields] = useState({});
	const webhookFieldMap = [
		{
			platform: "Razorpay",
			instructions: [
				"**Log in** to your **Razorpay Dashboard**.",
				"Go to **Settings** > **Webhooks**.",
				"Click **\"+ Add New Webhook\"**.",
				"**Paste the copied Webhook URL** into the designated URL field.",
				"**Select events based on the corresponding webhook description shown on the left.**",
				"Click **\"Create Webhook\"**."
			],
			webhooks: [
				{
					label: "Payment Success Webhook",
					url: "https://your-api.com/webhooks/razorpay/payment-success-prod",
					events: ["payment.captured"]
				},
				{
					label: "Refund Status Webhook",
					url: "https://your-api.com/webhooks/razorpay/refund-status-prod",
					events: ["refund.processed"]
				}
			]
		},
		{
			platform: "HDFC_SmartGateway",
			instructions: [
				"**Log in** to your **HDFC SmartGateway Merchant Panel**.",
				"Navigate to the **API Settings**, **Integrations**, or **Webhook Configuration** section. This path can vary.",
				"Look for fields related to **Callback URL**, **Notification URL**, or **Webhook Endpoint**.",
				"**Paste the copied Webhook URL** into the relevant field.",
				"**Select events based on the corresponding webhook description shown on the left.**",
				"**Save** or **Update** your settings. You might need to contact HDFC support for certain configurations."
			],
			webhooks: [
				{
					label: "Payment Notification Webhook",
					url: "https://your-api.com/webhooks/hdfc-smartgateway/payment-status-prod",
					events: ["payment.success", "payment.failed"]
				}
			]
		},
		{
			platform: "Cashfree",
			instructions: [
				"**Log in** to your [**Cashfree Merchant Dashboard**](https://merchant.cashfree.com/).",
				"Go to **Settings** > **Webhooks** from the left-hand menu.",
				"Click on **Add Webhook** (or **Edit** an existing one if already configured).",
				"In the **Webhook URL** field, **paste the copied URL** from the integration.",
				"In the **Events** section, select relevant events like `PAYMENT_SUCCESS_WEBHOOK`, `PAYMENT_FAILURE_WEBHOOK`, etc.",
				"Set the **Authentication Header** (if required by your backend) for added security.",
				"Click **Save** to update the webhook settings."
			],
			webhooks: [
				{
					label: "Cashfree Payment Webhook",
					url: "https://your-api.com/webhooks/cashfree/payment-status-prod",
					events: ["PAYMENT_SUCCESS_WEBHOOK", "PAYMENT_FAILURE_WEBHOOK"]
				}
			]
		}
	];

	const platformFieldMap = {
		Razorpay: [
			{ key: "key_id", label: "API Key", type: "text", required: true },
			{ key: "secret_key", label: "Secret Key", type: "text", required: true },
		],
		HDFC_SmartGateway: [
			{ key: "api_key", label: "API Key", type: "text", required: true },
			{ key: "merchant_id", label: "Merchant ID", type: "text", required: true },
			{ key: "response_key", label: "Response Key", type: "text", required: true },
		],
		Cashfree: [
			{ key: "api_key", label: "API Key", type: "text", required: true },
			{ key: "secret_key", label: "Secret Key", type: "text", required: true },
		]
	};

	const [paymentCredentialsSetUpModalShow, setPaymentCredentialsSetUpModalShow] = useState(false);
	const [defaultSetUpModalShow, setDefaultSetUpModalShow] = useState(false);
	const [paymentCredentials, setPaymentCredentials] = useState([]);
	const platformList = ["Razorpay", "HDFC_SmartGateway", "Cashfree"].map(x => ({ label: x, value: x }));

	const [accountId, setAccountId] = useState("")
	const [operationType, setOperationType] = useState("")

	const [defaultPaymentAccount, setDefaultPaymentAccount] = useState(false);
	const [contentFlag, setContentFlag] = useState(0);
	const [paymentId, setPaymentId] = useState("");

	const [mainSubmitButtonState, setMainSubmitButtonState] = useState(false)

	var page_name = "set-up-ecommerce-content-info";

	const { user, read, write, visibility, organization_id } = useAuthData(page_name);

	const loaderstyle = {
		display: "block",
		marginLeft: "auto",
		marginRight: "auto",
		marginTop: "auto",
		marginBottom: "auto",
	};

	useEffect(() => {
		if (read || write) {
			(user.email && visibility) && getPaymentCredentials();
		}
	}, [user, visibility, read, write])

	async function getPaymentCredentials() {
		var object = {};
		object.email = user.email;

		const response = await fetch(
			`${process.env.REACT_APP_SERVER_URL}/fetch-ecommerce-payemnt-details`, { method: 'POST', body: JSON.stringify({ "email": user.email, "page_name": page_name, "organization_id": organization_id }), headers: { "Content-type": "application/json; charset=UTF-8", "token": getCookie("access_token") } }
		);
		const body = await response.json();
		if (body.operation === "success") {
			setstate(1);
			if (body.data.length > 0) {
				setPaymentCredentials(body.data[0].payment_credentials);
				setPaymentId(body.data[0].unique_id);
				setContentFlag(1);
			}
		}
	}

	const handleSetDefault = (object) => {
		setAccountId(object.account_id);
		setPlatform(object.platform);
		setDefaultPaymentAccount(object.default);
		setDefaultSetUpModalShow(true);
	};

	const handleEditPaymentConfiguration = (object) => {
		setPaymentCredentialsSetUpModalShow(true);
		setOperationType("Edit");
		setAccountId(object.account_id);
		setPlatform(object.platform);
		setDefaultPaymentAccount(object.default);

		// Set dynamic platform fields
		const fields = platformFieldMap[object.platform] || [];
		const platformData = {};
		fields.forEach(field => {
			platformData[field.key] = object[field.key] || "";
		});
		setPlatformFields(platformData);
	};

	const handleAddPaymentConfiguration = () => {
		setPaymentCredentialsSetUpModalShow(true);
		setOperationType("Add");
		setAccountId(paymentCredentials.length + 1);
		setPlatform("Razorpay");
		setPlatformFields({}); // Reset fields on add

		const hasDefault = paymentCredentials.some(x => x.default === true);
		setDefaultPaymentAccount(!hasDefault);
	};

	const setDefaultPaymentConfig = () => {
		const values = {
			payment_id: paymentId,
			platform,
			default_payment_account: defaultPaymentAccount,
			account_id: accountId,
			page_name,
			organization_id
		};

		setMainSubmitButtonState(true)
		var headers = {
			headers: {
				"Content-Type": "application/json",
				"Accept": "*/*",
				"token": getCookie("access_token")
			}
		}

		axios.post(`${process.env.REACT_APP_SERVER_URL}/update-default-payment-config`, values, headers).then((res) => {
			setMainSubmitButtonState(false)
			if (res.data && res.data.operation == "success") {
				swal("Great!", `Payment credential saved successfully!`, "success").then((value) => {
					window.location.reload();
				});
			}
			else {
				swal("Oops!", "Something went wrong!", "error");
			}
		});
	}

	const updateSetupInfo = () => {
		if (platformFieldMap[platform]) {
			for (let field of platformFieldMap[platform]) {
				if (field.required && !platformFields[field.key]) {
					swal("Oops!", `${field.label} can't be blank`, "warning");
					return false; // Stop the function if validation fails
				}
			}
		}

		const values = {
			payment_id: paymentId,
			platform,
			operation_type: operationType,
			default_payment_account: defaultPaymentAccount,
			account_id: accountId,
			...platformFields,
			page_name,
			organization_id
		};

		setMainSubmitButtonState(true)
		var headers = {
			headers: {
				"Content-Type": "application/json",
				"Accept": "*/*",
				"token": getCookie("access_token")
			}
		}
		let api = `configure-ecommerce-payemnt-details`;
		if (contentFlag) {
			api = `update-ecommerce-payemnt-details`;
		}

		axios.post(`${process.env.REACT_APP_SERVER_URL}/${api}`, values, headers).then((res) => {
			setMainSubmitButtonState(false)
			if (res.data && res.data.operation == "success") {
				swal("Great!", `Payment credential saved successfully!`, "success").then((value) => {
					window.location.reload();
				});
			}
			else if (res.data.message == "wrong_credential") {
				swal("Oops!", "Your payment credentials are incorrect. Please review and enter the correct information !", "error");
			}
			else {
				swal("Oops!", "Something went wrong!", "error");
			}
		});
	}

	// Update current platform data when selectedPlatform changes
	useEffect(() => {
		if (platform) {
			const data = webhookFieldMap.find(p => p.platform === platform);
			setCurrentPlatformData(data);
		} else {
			setCurrentPlatformData(null);
		}
	}, [platform]);

	// Function to handle copying any given URL
	const handleCopyUrl = (urlToCopy) => {
		if (urlToCopy) {
			navigator.clipboard.writeText(urlToCopy)
				.then(() => {
					swal("Copied!", "Webhook URL copied to clipboard.", "success");
				})
				.catch(err => {
					console.error('Failed to copy webhook URL:', err);
					swal("Oops!", "Failed to copy URL. Please copy manually.", "error");
				});
		}
	};

	const handlePaymentConfigModalClose = () => {
		setPaymentCredentialsSetUpModalShow(false);
		setOperationType("");
		setAccountId("");
		setPlatform("");
		setPlatformFields({}); // Reset fields on add
		setDefaultPaymentAccount(false);
	}

	return (
		<>
			<div>
				<div className="d-flex flex-stack mb-5">
					<h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Payment Configuration Info</h1>
					<div className="d-flex align-items-center gap-2 gap-lg-3">
						<a onClick={() => { handleAddPaymentConfiguration() }} className="btn btn-sm btn-primary me-3 rounded btn-hover-scale">
							<span className="d-md-none">
								<svg width="15" height="15" viewBox="0 0 11 11" fill="none">
									<path d="M10.8756 6.56H6.50763V10.856H4.49163V6.56H0.123625V4.664H4.49163V0.344H6.50763V4.664H10.8756V6.56Z" fill="#fff"></path>
								</svg>
							</span>
							<span className="d-none d-md-inline">New Payment Config</span>
						</a>
					</div>
				</div>
				<div>
					{state === 1 ?
						<>
							<div className="form d-flex flex-column flex-lg-row">
								<div className="d-flex flex-column flex-row-fluid gap-7 gap-lg-10">
									<ul className="nav nav-custom nav-tabs nav-line-tabs nav-line-tabs-2x border-0 fs-4 fw-semibold mb-n2">
										<li className="nav-item">
											<a className={`nav-link text-active-primary pb-4 ${navtype === "Payment configuration" && "active"} cursor-pointer`} onClick={(e) => { setnavtype("Payment configuration"); }}>Payment configuration</a>
										</li>
										<li className="nav-item">
											<a className={`nav-link text-active-primary pb-4 ${navtype === "Webhook settings" && "active"} cursor-pointer`} onClick={(e) => { setnavtype("Webhook settings"); }}>Webhook settings</a>
										</li>
									</ul>
									<div className="tab-content">
										{navtype == "Payment configuration" &&
											<div className={`tab-pane fade ${navtype === "Payment configuration" && "show active"}`} role="tab-panel">
												<div className="card mb-5 mb-xl-8">
													<div className="card-body py-3">
														{paymentCredentials.length > 0 ? (
															<div className="d-flex flex-wrap align-items-center justify-content-center">
																{paymentCredentials.map((obj, index) => (
																	<div
																		className="card border border-border-2 rounded shadow m-3"
																		style={{ minHeight: "200px", minWidth: "300px" }}
																		key={obj.id || index}
																	>
																		<div className="card-header align-items-center d-flex justify-content-between" style={{ minHeight: "40px", padding: "10px" }}>
																			<h5 className="card-title mb-0">
																				<span className="badge badge-primary">{obj.platform}</span>
																			</h5>
																			{obj.default ? (
																				<span className="badge badge-success">Default</span>
																			) : (
																				<span className="badge badge-secondary">Not Default</span>
																			)}
																		</div>
																		<div className="card-body d-flex flex-column justify-content-between p-5">
																			{/* Display credential details based on the platform */}
																			{obj.platform === "Razorpay" && (
																				<>
																					<p className="text-gray-600">
																						<strong className="text-gray-800">API Key:</strong> {obj.key_id}
																					</p>
																					<p className="text-gray-600">
																						<strong className="text-gray-800">Secret Key:</strong> {obj.secret_key}
																					</p>
																				</>
																			)}

																			{obj.platform === "Cashfree" && (
																				<>
																					<p className="text-gray-600">
																						<strong className="text-gray-800">API Key:</strong> {obj.api_key}
																					</p>
																					<p className="text-gray-600">
																						<strong className="text-gray-800">Secret Key:</strong> {obj.secret_key}
																					</p>
																				</>
																			)}

																			{obj.platform === "HDFC_SmartGateway" && (
																				<>
																					<p className="text-gray-600">
																						<strong className="text-gray-800">Merchant ID:</strong> {obj.merchant_id}
																					</p>
																					<p className="text-gray-600">
																						<strong className="text-gray-800">API Key:</strong> {obj.api_key}
																					</p>
																					<p className="text-gray-600">
																						<strong className="text-gray-800">Response Key:</strong> {obj.response_key}
																					</p>
																				</>
																			)}

																			<div className="d-flex align-items-center gap-2 gap-lg-3 mt-auto">
																				<button
																					type="button"
																					className="btn btn-sm btn-light-primary"
																					onClick={() => handleEditPaymentConfiguration(obj)}
																					title="Edit Configuration"
																					aria-label="Edit Configuration"
																				>
																					<svg width="20" height="20" viewBox="0 0 1024 1024">
																						<g>
																							<path d="M261.057 569.88l187.155 187.155 436.658-436.663-187.158-187.158-436.656 436.666zM748.456 269.631c11.336 11.336 11.336 29.718 0 41.054l-306.693 306.691c-5.671 5.666-13.098 8.501-20.529 8.501-7.427 0-14.856-2.833-20.525-8.501-11.336-11.339-11.336-29.72 0-41.056l306.691-306.689c11.336-11.336 29.72-11.336 41.056 0z" fill="rgb(115, 208, 244)"></path>
																							<path d="M400.709 617.376c5.671 5.668 13.098 8.501 20.525 8.501 7.432 0 14.859-2.835 20.529-8.501l306.693-306.691c11.336-11.339 11.336-29.72 0-41.054-11.334-11.336-29.72-11.336-41.056 0l-306.691 306.689c-11.336 11.339-11.336 29.718 0 41.056z" fill="rgb(61, 104, 137)"></path>
																							<path d="M1010.864 185.012c-11.213-33.559-36.041-70.203-71.817-105.969-36.054-36.058-90.955-79.043-147.749-79.043-25.477 0-48.95 8.958-68.065 25.936-0.997 0.764-1.947 1.599-2.845 2.497l-110.135 110.132h-575.388c-16.032 0-29.031 12.998-29.031 29.031v827.374c0 16.032 12.998 29.031 29.031 29.031h827.374c16.032 0 29.031-12.998 29.031-29.031v-593.082c0-1.81-0.189-3.571-0.506-5.288l96.413-96.415c0.194-0.191 0.387-0.38 0.576-0.573c0.005-0.002 0.010-0.007 0.015-0.012l1.889-1.892c0.893-0.893 1.71-1.824 2.46-2.799 26.401-29.614 32.889-67.552 18.746-109.895zM395.108 786.044l-204.828 59.116 46.509-217.434 158.318 158.318zM448.213 757.035l-187.155-187.155 436.656-436.663 187.158 187.158-436.658 436.661zM833.209 965.939h-769.312v-769.312h488.301l-353.392 353.394c-3.96 3.96-6.692 8.98-7.862 14.457l-67.578 315.942c-2.136 9.979 1.13 20.348 8.595 27.308 5.448 5.076 12.546 7.795 19.799 7.795 1.551 0 560.096 0 560.096 0 16.037 0 29.031-12.998 29.031-29.031s-12.994-29.031-29.031-29.031h-354.821l95.237-27.487c4.715-1.362 9.007-3.895 12.478-7.364l368.458-368.458v511.787zM947.853 257.286c-0.317 0.339-0.624 0.685-0.922 1.035l-0.208 0.206c-0.099 0.099-0.203 0.198-0.302 0.298l-20.493 20.491-187.158-187.155 20.965-20.965c0.363-0.307 0.723-0.624 1.072-0.956 8.893-8.308 18.584-12.178 30.492-12.178 28.382 0 67.276 22.61 106.695 62.038 21.594 21.589 89.005 95.29 49.86 137.186z" fill="rgb(61, 104, 137)"></path>
																						</g>
																					</svg>
																					Edit
																				</button>

																				{!obj.default && (
																					<button
																						type="button"
																						className="btn btn-sm btn-light-success"
																						onClick={() => handleSetDefault(obj)} // Assuming you have an ID to set as default
																						title="Set as Default"
																						aria-label="Set as Default"
																					>
																						<span className="svg-icon svg-icon-2">
																							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
																								<path d="M9.83999 18.5101C9.64999 18.5101 9.46999 18.4301 9.32999 18.2901L4.03999 13.0001C3.74999 12.7101 3.74999 12.2301 4.03999 11.9401C4.32999 11.6501 4.80999 11.6501 5.09999 11.9401L9.83999 16.6801L18.9099 7.61007C19.1999 7.32007 19.6799 7.32007 19.9699 7.61007C20.2599 7.90007 20.2599 8.38007 19.9699 8.67007L10.3499 18.2901C10.2099 18.4301 10.0299 18.5101 9.83999 18.5101Z" fill="currentColor" />
																							</svg>
																						</span>
																						Set Default
																					</button>
																				)}
																			</div>
																		</div>
																	</div>
																))}
															</div>
														) : (
															<div className="notice d-flex bg-light-primary rounded border-primary border border-dashed rounded-3 p-6">
																<div className="d-flex flex-stack flex-grow-1">
																	<div className="fw-bold">
																		<h4 className="text-gray-800 fw-bolder">No Data Available.</h4>
																		<div className="fs-6 text-gray-600"> </div>
																	</div>
																</div>
															</div>
														)}
													</div>
												</div>
											</div>
										}
										{navtype == "Webhook settings" &&
											<div className={`tab-pane fade ${navtype === "Webhook settings" && "show active"}`} role="tab-panel">
												<div className="card mb-5 mb-xl-8">
													<div className="card-header border-0 pt-5">
														<h3 className="card-title align-items-start flex-column">
															<span className="card-label fw-bold fs-3 mb-1">Webhook Setup</span>
															<span className="text-muted mt-1 fw-semibold fs-7">Configure platform-specific webhooks for real-time updates.</span>
														</h3>
													</div>
													<div className="card-body py-3">
														<div className="row mb-10">
															<div className="col-xl-12"> {/* This column will now contain the dropdown and then split into two */}
																{/* <p className="fs-6 fw-semibold text-gray-600 py-4 m-0">
																	Select a payment platform from the dropdown to see its specific webhook setup instructions and associated webhook URLs.
																</p> */}

																{/* --- Platform Selection Dropdown --- */}
																<div className="mb-5">
																	<label className="required fs-5 fw-bold mb-2">Select Platform</label>
																	<div className="d-flex align-items-center"> {/* Use flexbox to align select and span */}
																		<div className="flex-grow-1 me-4">
																			<Select
																				styles={theme === 'dark' ? dropdownStyleDark : {}}
																				options={platformList}
																				onChange={(val) => {
																					setPlatform(val.value);
																				}}
																				value={platform ? { label: platform, value: platform } : null}
																				placeholder="Choose a platform..."
																			/>
																			<span className="text-muted fw-semibold fs-7">
																				Choose a platform to view its unique webhook configurations.
																			</span>
																		</div>
																	</div>
																</div>

																{/* --- Webhook Details and Instructions (Conditionally Rendered in two columns) --- */}
																{currentPlatformData && (
																	<div className="row mt-7"> {/* New row to split content */}
																		<div className="col-xl-6"> {/* Left Column for Webhook URLs */}
																			<h4 className="fw-bold text-dark mb-3">Webhook URLs for {currentPlatformData.platform}:</h4>
																			{currentPlatformData.webhooks.length > 0 ? (
																				<div className="list-group">
																					{currentPlatformData.webhooks.map((webhook, index) => (
																						<div
																							key={index}
																							className="list-group-item d-flex flex-column p-3 mb-2 rounded bg-light-info border-info border"
																						>
																							<div className="d-flex justify-content-between align-items-center mb-2">
																								<div>
																									<strong className="text-dark">{webhook.label}:</strong>
																									<span className="ms-2 text-primary">{webhook.url}</span>
																								</div>
																								<button
																									className="btn btn-sm btn-info fw-bold flex-shrink-0"
																									onClick={() => handleCopyUrl(webhook.url)}
																								>
																									Copy
																								</button>
																							</div>
																							{webhook.events && webhook.events.length > 0 && (
																								<ul className="mb-0 ps-3">
																									{webhook.events.map((event, i) => (
																										<li key={i} className="fs-7 text-gray-700">
																											<code>{event}</code>
																										</li>
																									))}
																								</ul>
																							)}
																						</div>
																					))}

																				</div>
																			) : (
																				<p className="text-gray-600">No specific webhooks listed for this platform yet.</p>
																			)}
																		</div>

																		<div className="col-xl-6"> {/* Right Column for Setup Instructions */}
																			<div className="p-5 border rounded bg-light-primary">
																				<h4 className="fw-bold text-primary mb-3">How to Set Up Webhooks for {currentPlatformData.platform}:</h4>
																				<ol className="mb-0 fs-7 text-gray-700">
																					{currentPlatformData.instructions.map((instruction, index) => (
																						<li key={index} dangerouslySetInnerHTML={{ __html: instruction }}></li>
																					))}
																				</ol>
																				<p className="mt-3 mb-0 fs-7 text-gray-600">
																					**Note:** Always refer to the official **{currentPlatformData.platform}** documentation for the most up-to-date and detailed instructions, including any required webhook secrets or verification processes.
																				</p>
																			</div>
																		</div>
																	</div>
																)}
															</div>
														</div>
													</div>
												</div>
											</div>
										}
									</div>
								</div>
							</div>
						</>
						:
						<div className="card modal-body pt-0 pb-15 px-5 px-xl-20">
							<br />

							{state === 2 ?
								<img src="/images/loader-06.svg" alt="" style={loaderstyle} />
								:
								<PageError />
							}
						</div>
					}
				</div>
			</div>
			<Modal show={paymentCredentialsSetUpModalShow} onHide={() => { handlePaymentConfigModalClose() }} centered>
				<Modal.Header>
					<Modal.Title>{operationType == "Add" ? <>Add</> : <>Update</>} Payment Credential</Modal.Title>
					<div className="btn btn-sm btn-icon btn-active-color-primary" onClick={() => { handlePaymentConfigModalClose() }}>
						<span className="svg-icon svg-icon-1">
							<svg xmlns="http://www.w3.org/2000/svg" xlink="http://www.w3.org/1999/xlink" width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
								<g transform="translate(12.000000, 12.000000) rotate(-45.000000) translate(-12.000000, -12.000000) translate(4.000000, 4.000000)" fill="#000000">
									<rect fill="#000000" x="0" y="7" width="16" height="2" rx="1" />
									<rect fill="#000000" opacity="0.5" transform="translate(8.000000, 8.000000) rotate(-270.000000) translate(-8.000000, -8.000000)" x="0" y="7" width="16" height="2" rx="1" />
								</g>
							</svg>
						</span>
					</div>
				</Modal.Header>
				<Modal.Body>
					<div className='container px-1'>
						<div className="row mb-5">
							<div className="col-12">
								<label className="required fs-5 fw-bold mb-2">Platform</label>
								<Select
									styles={theme === 'dark' ? dropdownStyleDark : {}}
									options={platformList}
									onChange={(val) => {
										if (operationType == "Edit") {
											const temp = paymentCredentials.find(x => x.platform == val.value);
											handleEditPaymentConfiguration(temp);
										}
										else {
											setPlatform(val.value);
										}
									}}
									value={{ label: platform, value: platform }}
								/>
							</div>
						</div>
						{platformFieldMap[platform]?.map((field) => (
							<div className="row mb-5" key={field.key}>
								<div className="col-12">
									<label className={`fs-5 fw-bold mb-2 ${field.required ? "required" : ""}`}>{field.label}</label>
									<input
										type={field.type}
										className="form-control form-control-solid"
										value={platformFields[field.key] || ""}
										onChange={(e) => setPlatformFields({ ...platformFields, [field.key]: e.target.value })}
										placeholder={field.label}
									/>
								</div>
							</div>
						))}
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button className="btn btn-secondary rounded" onClick={() => { handlePaymentConfigModalClose() }}>Close</button>
					{
						write &&
						<button className="btn btn-success rounded" data-kt-indicator={mainSubmitButtonState ? "on" : "off"} disabled={mainSubmitButtonState} onClick={() => { updateSetupInfo() }}>
							<span className="indicator-label">{operationType == "Add" ? <>+ Add</> : <>Update</>}</span>
							<span className="indicator-progress">Please wait...
								<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
							</span>
						</button>
					}
				</Modal.Footer>
			</Modal>

			<Modal show={defaultSetUpModalShow} onHide={() => { setDefaultSetUpModalShow(false); }} centered>
				<Modal.Header>
					<Modal.Title>Set Default Payment Configuration</Modal.Title>
					<div className="btn btn-sm btn-icon btn-active-color-primary" onClick={() => { setDefaultSetUpModalShow(false); }}>
						<span className="svg-icon svg-icon-1">
							<svg xmlns="http://www.w3.org/2000/svg" xlink="http://www.w3.org/1999/xlink" width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
								<g transform="translate(12.000000, 12.000000) rotate(-45.000000) translate(-12.000000, -12.000000) translate(4.000000, 4.000000)" fill="#000000">
									<rect fill="#000000" x="0" y="7" width="16" height="2" rx="1" />
									<rect fill="#000000" opacity="0.5" transform="translate(8.000000, 8.000000) rotate(-270.000000) translate(-8.000000, -8.000000)" x="0" y="7" width="16" height="2" rx="1" />
								</g>
							</svg>
						</span>
					</div>
				</Modal.Header>
				<Modal.Body>
					<div className="row mb-0">
						{/* Contextual text before the switch */}
						<p className="text-gray-700 mb-4 **fs-5**">
							Would you like to make this **{platform}** payment configuration your default?
							This will be used for all future transactions unless specified otherwise.
						</p>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button className="btn btn-secondary" onClick={() => { setDefaultSetUpModalShow(false); }}>Close</button>
					{
						(write && !defaultPaymentAccount) &&
						<button type="button" className="btn btn-info" data-kt-indicator={mainSubmitButtonState ? "on" : "off"} disabled={mainSubmitButtonState} onClick={() => { setDefaultPaymentConfig() }}>
							<span className="indicator-label">Set as Default</span>
							<span className="indicator-progress">Please wait...
								<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
							</span>
						</button>
					}
				</Modal.Footer>
			</Modal>
		</>
	);
}
export default SetUpEcommercePaymentInfo;